import React from "react";

/**
 * TimelinessBreakdown — per-scenario timeliness summary shown at the top of the
 * validation results view. Reads the server-computed `timelinessSummary`
 * (counts + rates only, no PHI). Renders nothing when a universe has no
 * timeliness scenarios (e.g. non-ODAG/CDAG protocols).
 *
 * Theme: matches the app's rose/orange chrome (white/blur cards, rounded-2xl,
 * gradient header), with emerald/red only as the semantic compliance signal on
 * the rate badges.
 */

function rateClasses(rate) {
  if (rate === null || rate === undefined) return "bg-slate-100 text-slate-500 border-slate-200";
  if (rate >= 95) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (rate >= 85) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-700 border-red-200";
}

function RateBadge({ rate, size = "sm" }) {
  const text = rate === null || rate === undefined ? "N/A" : `${rate}%`;
  const pad = size === "lg" ? "px-4 py-2 text-2xl" : "px-2.5 py-0.5 text-sm";
  return (
    <span className={`inline-flex items-center justify-center rounded-full border font-bold ${pad} ${rateClasses(rate)}`}>
      {text}
    </span>
  );
}

/**
 * Month-by-month table for universes whose clocks span months (SNPE: an IHRA
 * lands in its enrollment month, an AHRA in the month it was due). The same
 * per-month numbers Precision COA scores after approval — what compliance
 * sees here is what syncs there.
 */
function MonthlyBreakdown({ monthly }) {
  const rows = (monthly || [])
    .map((m) => ({ ...m, rate: (m.timely + m.untimely) > 0 ? Math.round((m.timely / (m.timely + m.untimely)) * 1000) / 10 : null }))
    .sort((a, b) => (a.month === b.month ? String(a.scenario).localeCompare(b.scenario) : String(a.month).localeCompare(b.month)));
  if (!rows.length) return null;
  const fmtMonth = (ym) => {
    const [y, m] = String(ym).split("-").map(Number);
    return Number.isFinite(y) && Number.isFinite(m)
      ? new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })
      : String(ym);
  };
  return (
    <div className="mt-5">
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Monthly timeliness</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 font-semibold uppercase text-xs tracking-wider">Month</th>
              <th className="py-2 px-3 font-semibold uppercase text-xs tracking-wider">Scenario</th>
              <th className="py-2 px-3 font-semibold uppercase text-xs tracking-wider text-right">Timely</th>
              <th className="py-2 px-3 font-semibold uppercase text-xs tracking-wider text-right">Untimely</th>
              <th className="py-2 pl-3 font-semibold uppercase text-xs tracking-wider text-right">Timeliness</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={`${m.month}|${m.scenario}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                <td className="py-2 pr-3 font-medium text-slate-800 whitespace-nowrap">{fmtMonth(m.month)}</td>
                <td className="py-2 px-3 text-slate-600">{m.scenario}</td>
                <td className="py-2 px-3 text-right font-semibold text-emerald-700">{m.timely}</td>
                <td className={`py-2 px-3 text-right font-semibold ${m.untimely > 0 ? "text-red-600" : "text-slate-400"}`}>{m.untimely}</td>
                <td className="py-2 pl-3 text-right"><RateBadge rate={m.rate} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        Initial HRAs are counted in their enrollment month; annual HRAs in the month the assessment was due. After approval, each month here scores its matching Precision COA monitoring row.
      </p>
    </div>
  );
}

export default function TimelinessBreakdown({ timelinessSummary, monthlyTimeliness = null }) {
  const scenarios = timelinessSummary?.scenarios || [];
  if (!scenarios.length) return null;

  const overall = timelinessSummary.overall || { timely: 0, untimely: 0, applicable: 0, rate: null };
  const dismissed = timelinessSummary.dismissed || 0;
  const indeterminate = timelinessSummary.indeterminate || 0;

  return (
    <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg shadow-slate-200/40 overflow-hidden">
      {/* Header — app rose/orange chrome */}
      <div className="px-6 py-4 bg-gradient-to-r from-rose-500 to-orange-600">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="h-3 w-3 rounded-full bg-white/90 shadow-[0_0_0_5px_rgba(255,255,255,0.2)]" />
            <div>
              <h3 className="text-lg font-black tracking-tight text-white">Timeliness by Category</h3>
              <p className="text-xs text-white/80 font-medium">Universe-level, per CMS protocol</p>
            </div>
          </div>
          {/* General timeliness hero */}
          <div className="flex items-center gap-3 bg-white/15 rounded-xl px-4 py-2 border border-white/25">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">General timeliness</div>
              <div className="text-xs text-white/90 font-medium">
                {overall.timely} of {overall.applicable} timely
              </div>
            </div>
            <RateBadge rate={overall.rate} size="lg" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3 font-semibold uppercase text-xs tracking-wider">Scenario</th>
                <th className="py-2 px-3 font-semibold uppercase text-xs tracking-wider text-right">Timely</th>
                <th className="py-2 px-3 font-semibold uppercase text-xs tracking-wider text-right">Untimely</th>
                <th className="py-2 px-3 font-semibold uppercase text-xs tracking-wider text-right">N/A</th>
                <th className="py-2 pl-3 font-semibold uppercase text-xs tracking-wider text-right">Timeliness</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={s.scenario} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors">
                  <td className="py-2.5 pr-3 font-medium text-slate-800">{s.scenario}</td>
                  <td className="py-2.5 px-3 text-right font-semibold text-emerald-700">{s.timely}</td>
                  <td className={`py-2.5 px-3 text-right font-semibold ${s.untimely > 0 ? "text-red-600" : "text-slate-400"}`}>{s.untimely}</td>
                  <td className="py-2.5 px-3 text-right text-slate-400">{s.notApplicable}</td>
                  <td className="py-2.5 pl-3 text-right"><RateBadge rate={s.rate} /></td>
                </tr>
              ))}
              <tr className="bg-gradient-to-r from-rose-50/70 to-orange-50/60">
                <td className="py-3 pr-3 font-black text-slate-900">General timeliness (all categories)</td>
                <td className="py-3 px-3 text-right font-black text-emerald-700">{overall.timely}</td>
                <td className={`py-3 px-3 text-right font-black ${overall.untimely > 0 ? "text-red-600" : "text-slate-500"}`}>{overall.untimely}</td>
                <td className="py-3 px-3 text-right text-slate-400">—</td>
                <td className="py-3 pl-3 text-right"><RateBadge rate={overall.rate} /></td>
              </tr>
            </tbody>
          </table>
        </div>

        <MonthlyBreakdown monthly={monthlyTimeliness} />

        {(dismissed > 0 || indeterminate > 0) && (
          <p className="mt-3 text-xs text-slate-500 leading-relaxed">
            <span className="font-semibold text-slate-600">Excluded from timeliness per CMS protocol:</span>{" "}
            {dismissed > 0 && `${dismissed} dismissed case${dismissed === 1 ? "" : "s"}`}
            {dismissed > 0 && indeterminate > 0 && " · "}
            {indeterminate > 0 && `${indeterminate} with an unrecognized determination`}
            . N/A counts cases this scenario could not be measured for (missing or invalid dates).
          </p>
        )}
      </div>
    </div>
  );
}
